import { computed, inject, Injectable, Signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { AppDownloadService } from '@ftr/api-core'
import { API_CONFIGURATION } from '@ftr/api-shared'
import { Uuid } from '@ftr/contracts/type/shared'
import { unwrapData } from '@ftr/foundation'
import { APP_MODE, AppPaths } from '@ftr/routing-paths'
import { featureFlag } from '@ftr/ui-feature-flags'
import { OrganizationState } from '@ftr/ui-organization'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { combineLatest, filter, map, startWith } from 'rxjs'
import { HelpSubMenuItem } from './help-sub-menu-flyout/item/help-sub-menu-flyout-item.component'
import {
  getCourtsHelpSupport,
  getLoggedOutCourtsHelpSupport,
  getLoggedOutTopNavHelpSupport,
  getPublicPortalHelpSupport,
} from './help-support/items'

@Injectable({ providedIn: 'root' })
export class HelpMenuItemsProvider {
  private readonly appDownloadService = inject(AppDownloadService)
  private readonly apiConfiguration = inject(API_CONFIGURATION)
  private readonly store = inject(Store)
  private readonly router = inject(Router)
  private readonly appMode = inject(APP_MODE)

  private readonly isFtrForumEnabled = featureFlag('ftr-forum')
  private readonly isPublicNavigationEnabled = featureFlag('public-navigation')

  private readonly isLoggedIn = toSignal(this.store.select(UserState.isLoggedIn), { initialValue: false })
  private readonly planTier = toSignal(
    combineLatest([
      this.store
        .select(OrganizationState.currentOrganization)
        .pipe(map(organization => organization?.subscriptionPlan.planTier)),
      this.store.select(UserState.user).pipe(
        unwrapData(),
        map(user => user.individual?.planTier),
      ),
    ]).pipe(map(([orgTier, userTier]) => orgTier || userTier)),
    { initialValue: undefined },
  )
  private readonly courtSystems = toSignal(this.store.select(UserState.courtSystems).pipe(unwrapData()), {
    initialValue: [],
  })
  private readonly getEnabledPermissions = toSignal(this.store.select(UserState.getEnabledPermissions))
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  )

  provide(courtSystemId?: Uuid): Signal<HelpSubMenuItem[]> {
    return computed(() => this.computeItems(courtSystemId))
  }

  private computeItems(courtSystemId: Uuid | undefined): HelpSubMenuItem[] {
    const desktopAppDownloadUrl = this.appDownloadService.getStandaloneAppLink()
    switch (this.appMode()) {
      case 'public':
        return getPublicPortalHelpSupport({
          desktopAppDownloadUrl,
          isFtrForumEnabled: this.isFtrForumEnabled(),
          courtsAppUrl: this.apiConfiguration.courtsAppUrl,
          isLoggedIn: this.isLoggedIn(),
          planTier: this.planTier(),
        })
      case 'courts': {
        if (!this.isLoggedIn()) {
          return getLoggedOutCourtsHelpSupport({
            desktopAppDownloadUrl,
            publicPortalUrl: this.apiConfiguration.publicAppUrl || undefined,
            isFtrForumEnabled: this.isFtrForumEnabled(),
          })
        }
        const isInternal = this.isInternalCourtsUser(courtSystemId)
        const hasCourtSystems = isInternal ? true : (this.courtSystems()?.length ?? 0) > 0
        const publicAppUrl = this.apiConfiguration.publicAppUrl
        const publicPortalUrl =
          isInternal && this.isPublicNavigationEnabled() && publicAppUrl ? publicAppUrl : undefined
        return getCourtsHelpSupport({
          desktopAppDownloadUrl,
          isFtrForumEnabled: this.isFtrForumEnabled(),
          hasCourtSystems,
          publicPortalUrl,
        })
      }
      case 'legacy':
      default:
        if (this.isLoggedIn()) {
          const isInternal = this.isInternalCourtsUser(courtSystemId)
          const hasCourtSystems = isInternal ? true : (this.courtSystems()?.length ?? 0) > 0
          return getCourtsHelpSupport({
            desktopAppDownloadUrl,
            isFtrForumEnabled: this.isFtrForumEnabled(),
            hasCourtSystems,
          })
        }
        return getLoggedOutTopNavHelpSupport({ desktopAppDownloadUrl })
    }
  }

  private isInternalCourtsUser(courtSystemId: Uuid | undefined): boolean {
    if (!courtSystemId) {
      return false
    }
    const url = this.currentUrl() ?? ''
    if (url.includes(`/${AppPaths.Request}/`)) {
      return false
    }
    const getPerms = this.getEnabledPermissions()
    return !!getPerms && getPerms(courtSystemId).length > 0
  }
}
